package cc.easyandroid.easycompose.component.router.stack

import cc.easyandroid.easycompose.component.Child
import cc.easyandroid.easycompose.component.GenericComponentContext
import cc.easyandroid.easycompose.component.router.children.ChildNavState.Status
import cc.easyandroid.easycompose.component.router.children.NavState
import cc.easyandroid.easycompose.component.router.children.NavigationSource
import cc.easyandroid.easycompose.component.router.children.SimpleChildNavState
import cc.easyandroid.easycompose.component.router.children.children
import cc.easyandroid.easycompose.component.value.Value
import cc.easyandroid.easycompose.core.statekeeper.SerializableContainer
import cc.easyandroid.easycompose.core.statekeeper.consumeRequired
import kotlinx.serialization.KSerializer
import kotlinx.serialization.builtins.ListSerializer

fun <Ctx : GenericComponentContext<Ctx>, C : Any, T : Any> Ctx.childStack(
    source: NavigationSource<StackNavigation.Event<C>>,
    serializer: KSerializer<C>?,
    initialStack: () -> List<C>,
    key: String = "DefaultChildStack",
    handleBackButton: Boolean = false,
    childFactory: (configuration: C, Ctx) -> T,
): Value<ChildStack<C, T>> =
    childStack(
        source = source,
        saveStack = { stack ->
            if (serializer != null) {
                SerializableContainer(value = stack, strategy = ListSerializer(serializer))
            } else {
                null
            }
        },
        restoreStack = { container ->
            if (serializer != null) {
                container.consumeRequired(strategy = ListSerializer(serializer))
            } else {
                null
            }
        },
        initialStack = initialStack,
        key = key,
        handleBackButton = handleBackButton,
        childFactory = childFactory,
    )

fun <Ctx : GenericComponentContext<Ctx>, C : Any, T : Any> Ctx.childStack(
    source: NavigationSource<StackNavigation.Event<C>>,
    serializer: KSerializer<C>?,
    initialConfiguration: C,
    key: String = "DefaultChildStack",
    handleBackButton: Boolean = false,
    childFactory: (configuration: C, Ctx) -> T
): Value<ChildStack<C, T>> =
    childStack(
        source = source,
        serializer = serializer,
        initialStack = { listOf(initialConfiguration) },
        key = key,
        handleBackButton = handleBackButton,
        childFactory = childFactory,
    )

fun <Ctx : GenericComponentContext<Ctx>, C : Any, T : Any> Ctx.childStack(
    source: NavigationSource<StackNavigation.Event<C>>,
    initialStack: () -> List<C>,
    saveStack: (List<C>) -> SerializableContainer?,
    restoreStack: (SerializableContainer) -> List<C>?,
    key: String = "DefaultChildStack",
    handleBackButton: Boolean = false,
    childFactory: (configuration: C, Ctx) -> T,
): Value<ChildStack<C, T>> =
    children(
        source = source,
        key = key,
        initialState = { StackNavState(configurations = initialStack()) },
        saveState = { saveStack(it.configurations) },
        restoreState = { container -> restoreStack(container)?.let(::StackNavState) },
        navTransformer = { state, event -> StackNavState(configurations = event.transformer(state.configurations)) },
        stateMapper = { _, children ->
            @Suppress("UNCHECKED_CAST")
            val createdChildren = children as List<Child.Created<C, T>>

            ChildStack(
                active = createdChildren.last(),
                backStack = createdChildren.dropLast(1),
            )
        },
        onEventComplete = { event, newState, oldState ->
            event.onComplete(newState.configurations, oldState.configurations)
        },
        backTransformer = { state ->
            if (handleBackButton && (state.configurations.size > 1)) {
                { StackNavState(configurations = state.configurations.dropLast(1)) }
            } else {
                null
            }
        },
        childFactory = childFactory,
    )

private data class StackNavState<out C : Any>(
    val configurations: List<C>,
) : NavState<C> {

    init {
        require(configurations.isNotEmpty()) { "Configuration stack must not be empty" }
    }

    override val children: List<SimpleChildNavState<C>> =
        configurations.mapIndexed { index, configuration ->
            SimpleChildNavState(
                configuration = configuration,
                status = if (index == configurations.lastIndex) Status.RESUMED else Status.CREATED,
            )
        }
}
